// PRESERVED FOR FUTURE USE — Stripe price/product IDs from the previous tier structure.
// Restore when pricing returns. Original tiers: Basic, Pro, Premium with associated checkout flow.
// Basic:   priceId   "price_1TJIAb1jlOuZdIhv0Yw72xnn"
// Pro:     priceId   "price_1TJGmg1jlOuZdIhvQXBFR5yN"
//          productId "prod_UHqTeMxVc5tpNW"
// Premium: priceId   "price_1TJGrc1jlOuZdIhvWnTjBhEE"
//          productId "prod_UHqYxRcflK438o"

import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldErrors = {
  name?: string;
  email?: string;
};

const INCLUSIONS: { title: string; body: string }[] = [
  {
    title: "Consent-first capture",
    body: "A guided interview where the subject records their voice, presence, and story, governed by the Digital Legacy Directive.",
  },
  {
    title: "3D photogrammetry tribute",
    body: "A scanned likeness viewable on any device, accessible in AR via QR code.",
  },
  {
    title: "Family tree placement",
    body: "Your loved one takes their place in a family tree where every name has a voice.",
  },
  {
    title: "Year one interactions",
    body: "Conversations with the tribute through their preserved voice, paid via the Legacy Package for the first year; the Preservation Fund thereafter.",
  },
  {
    title: "Perpetual preservation",
    body: "The Preservation Fund covers storage, hosting, and stewardship for generations — funded once, never billed again.",
  },
  {
    title: "The Digital Legacy Directive",
    body: "The consent framework that governs every tribute we create, with the subject as the source of truth.",
  },
];

const Pricing = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [buying, setBuying] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const validate = (): boolean => {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = "Please enter your name.";
    if (!email.trim()) next.email = "Please enter your email.";
    else if (!EMAIL_RE.test(email.trim()))
      next.email = "Please enter a valid email address.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "waitlist-signup",
        {
          body: {
            name: name.trim(),
            email: email.trim(),
            source: "pricing",
          },
        }
      );
      if (error) throw error;
      setIsDuplicate(data?.duplicate === true);
      setSubmitted(true);
    } catch (err) {
      const description =
        err instanceof Error ? err.message : "Please try again in a moment.";
      toast({
        title: "Could not join the waitlist",
        description,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Pay-first Buy CTA. Logged-in buyers go to Stripe Checkout via create-checkout;
  // logged-out buyers sign up first (no auto-resume in v1).
  const handleBuy = async () => {
    const priceId = import.meta.env.VITE_STRIPE_PRICE_ID;
    if (!priceId) {
      toast({ title: "Checkout is not configured yet", variant: "destructive" });
      return;
    }
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/signup");
      return;
    }
    // A STORED-but-stale session is common in a normal browser (iOS ITP evicts
    // localStorage; a backgrounded tab pauses autoRefreshToken), so the token
    // attached to invoke would be expired -> create-checkout 401/500. Force a
    // refresh so supabase-js sends a fresh access_token. If the session can't be
    // refreshed — refreshError, a null session, OR a thrown rejection (an invalid/
    // expired/revoked refresh token) — the stored session is POISONED: clear it and
    // re-authenticate instead of surfacing an opaque error. refreshSession is
    // awaited in its OWN try/catch so a thrown rejection can never escape as an
    // unhandled promise rejection (the bug this fixes).
    try {
      const { data: refreshed, error: refreshError } =
        await supabase.auth.refreshSession();
      if (refreshError || !refreshed.session) {
        await supabase.auth.signOut();
        setBuying(false);
        navigate("/signup");
        return;
      }
    } catch {
      await supabase.auth.signOut();
      setBuying(false);
      navigate("/signup");
      return;
    }

    setBuying(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });
      if (error) throw error;
      window.location.href = data.url;
    } catch (err) {
      // A 401/403 means the server rejected the token (stale/invalid despite the
      // refresh) — clear the poisoned session and re-authenticate rather than
      // showing an opaque error. Read the status the way CreateTribute does
      // (err.context.status). Any OTHER error (network / 500) is transient: keep
      // the user signed in and just surface the toast.
      const status = (err as { context?: Response }).context?.status;
      if (status === 401 || status === 403) {
        await supabase.auth.signOut();
        setBuying(false);
        navigate("/signup");
        return;
      }
      const description =
        err instanceof Error ? err.message : "Please try again in a moment.";
      toast({ title: "Could not start checkout", description, variant: "destructive" });
      setBuying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <img src="/ll-mark-256.png" alt="LegacyLens AI" className="h-10" />
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm">Sign In</Button>
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-6 py-20 max-w-2xl">
        {/* 1. Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-4">
            Legacy Package
          </h1>
          <p className="font-display text-2xl text-foreground mb-3">
            $800 <span className="text-lg text-muted-foreground">— one-time</span>
          </p>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Preserve a parent's or grandparent's legacy. No subscriptions. No renewals.
          </p>
        </motion.div>

        {/* 2. Itemized breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12 rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50 p-6 sm:p-8"
        >
          <ul className="space-y-4">
            <li className="flex justify-between gap-4">
              <span className="text-foreground">
                <span className="font-medium">Tribute creation</span>
                <span className="block text-sm text-muted-foreground">
                  creation, capture, and the tribute itself
                </span>
              </span>
              <span className="font-medium text-foreground whitespace-nowrap">$500</span>
            </li>
            <li className="flex justify-between gap-4">
              <span className="text-foreground">
                <span className="font-medium">Preservation Fund</span>
                <span className="block text-sm text-muted-foreground">
                  perpetual storage, hosting, and stewardship
                </span>
              </span>
              <span className="font-medium text-foreground whitespace-nowrap">$300</span>
            </li>
            <li className="flex justify-between gap-4 border-t border-border/50 pt-4">
              <span className="font-semibold text-foreground">Total</span>
              <span className="font-semibold text-foreground whitespace-nowrap">$800</span>
            </li>
          </ul>
        </motion.div>

        {/* Buy CTA — pay-first checkout, alongside the waitlist below */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mb-12"
        >
          <Button onClick={handleBuy} disabled={buying} size="lg" className="w-full">
            {buying ? "Starting checkout..." : "Preserve a Legacy"}
          </Button>
        </motion.div>

        {/* 3. Inclusion list */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-12"
        >
          <h2 className="font-display text-2xl font-semibold text-foreground mb-6">
            Every Legacy Package includes:
          </h2>
          <ul className="space-y-5">
            {INCLUSIONS.map((item) => (
              <li key={item.title}>
                <p className="font-medium text-foreground">{item.title}</p>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {item.body}
                </p>
              </li>
            ))}
          </ul>
          <p className="mt-6 leading-relaxed text-foreground/90">
            No subscriptions. No hidden fees. Your loved one's tribute is theirs,
            and your family's, forever.
          </p>
        </motion.div>

        {/* 4. Founding customers */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="font-display text-2xl font-semibold text-foreground mb-3">
            Founding customers
          </h2>
          <p className="leading-relaxed text-muted-foreground">
            LegacyLens opens for families this fall. We're working with a small
            group of funeral home and cemetery partners to bring the first
            tributes to families. If you'd like to be among the first, join the
            waitlist below.
          </p>
        </motion.div>

        {/* 5. Inline waitlist form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50 p-6 sm:p-8"
        >
          {submitted ? (
            <div>
              <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                {isDuplicate ? "You're already on the list" : "You're on the waitlist"}
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                We'll email you when LegacyLens opens for families. Until then, you
                can read the{" "}
                <Link
                  to="/digital-legacy-directive"
                  className="text-primary hover:underline"
                >
                  Digital Legacy Directive
                </Link>{" "}
                — the consent framework that governs every tribute we create.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pricing-name">Name</Label>
                <Input
                  id="pricing-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  aria-invalid={!!errors.name}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="pricing-email">Email</Label>
                <Input
                  id="pricing-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Joining..." : "Join the waitlist"}
              </Button>
            </form>
          )}
        </motion.div>

        {/* 6. Footer reminder */}
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Questions before joining?{" "}
          <Link to="/contact" className="text-primary hover:underline">
            Contact us
          </Link>
          .
        </p>
      </div>
    </div>
  );
};

export default Pricing;
